import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useAuthForm } from "@/hooks/use-auth-form";
import SpamFolderAlert from "./SpamFolderAlert";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";
import CustomCaptcha from "./CustomCaptcha";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Mail, KeyRound } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const AuthForm: React.FC = () => {
  const { form, isLoading, mode, toggleMode, onSubmit, showSpamAlert, showCaptcha, setCaptchaValid, onPasswordReset } = useAuthForm();
  const [captchaValue, setCaptchaValue] = useState("");
  const [isCaptchaValid, setIsCaptchaValid] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [changePasswordSuccess, setChangePasswordSuccess] = useState(false);
  const [changePasswordLoading, setChangePasswordLoading] = useState(false);
  
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { supabase } = useAuth();
  
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const type = searchParams.get('type') || hash.get('type');
    const tokenHash = searchParams.get('token_hash');
    const isRecovery =
      type !== 'signup' &&
      (type === 'recovery' ||
        hash.has('access_token') ||
        !!tokenHash ||
        searchParams.has('code'));


    if (isRecovery) {
      setShowChangePassword(true);
      setShowResetPassword(false);
    } else if (searchParams.has('reset')) {
      setShowResetPassword(true);
    }

    // Token-hash links: verify the OTP to open a short-lived recovery session
    // so the user can set a new password (no silent auto-login elsewhere).
    if (tokenHash && type === 'signup') {
      supabase.auth
        .verifyOtp({ type: 'signup', token_hash: tokenHash })
        .then(({ error }) => {
          setShowChangePassword(false);
          if (error) {
            toast.error('This confirmation link is invalid or has expired.');
          } else {
            toast.success('Email confirmed — you are signed in!');
            window.history.replaceState({}, '', '/auth');
          }
        });
    } else if (tokenHash) {
      supabase.auth
        .verifyOtp({ type: 'recovery', token_hash: tokenHash })
        .then(({ error }) => {
          if (error) {
            toast.error('This reset link is invalid or has expired. Please request a new one.');
            setShowChangePassword(false);
            setShowResetPassword(true);
          } else {
            // Clean the token out of the URL.
            window.history.replaceState({}, '', '/auth?type=recovery');
          }
        });
    }


    // Supabase strips the hash before React mounts in some flows, so also
    // listen for the recovery event.
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowChangePassword(true);
        setShowResetPassword(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [searchParams, supabase]);


  
  const resetForm = useForm<{ email: string }>({
    resolver: zodResolver(
      z.object({
        email: z.string().email("Please enter a valid email address"),
      })
    ),
    defaultValues: {
      email: "",
    },
  });
  
  const changePasswordForm = useForm<{ password: string, confirmPassword: string }>({
    resolver: zodResolver(
      z.object({
        password: z.string().min(8, "Password must be at least 8 characters long"),
        confirmPassword: z.string().min(8, "Password must be at least 8 characters long"),
      }).refine((data) => data.password === data.confirmPassword, {
        message: "Passwords do not match",
        path: ["confirmPassword"],
      })
    ),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });
  
  const handleCaptchaChange = (value: string) => {
    setCaptchaValue(value);
  };
  
  const handleCaptchaValidityChange = (isValid: boolean) => {
    setIsCaptchaValid(isValid);
    setCaptchaValid(isValid);
  };

  const handleResetPassword = async (values: { email: string }) => {
    setResetLoading(true);
    try {
      await onPasswordReset(values.email);
      setResetSent(true);
    } catch (error) {
      console.error("Error sending reset email:", error);
    } finally {
      setResetLoading(false);
    }
  };
  
  const handleChangePassword = async (values: { password: string }) => {
    setChangePasswordLoading(true);
    try {
      console.log("Updating password");
      const { error } = await supabase.auth.updateUser({ 
        password: values.password 
      });
      
      if (error) throw error;
      
      setChangePasswordSuccess(true);
      toast.success("Your password has been updated successfully");
    } catch (error: any) {
      console.error("Error updating password:", error);
      toast.error(error.message || "Failed to update password");
    } finally {
      setChangePasswordLoading(false);
    }
  };

  return (
    <Card className="w-full shadow-lg">
      <CardHeader>
        <CardTitle>
          {showChangePassword 
            ? "Set New Password" 
            : showResetPassword 
              ? "Reset Password" 
              : mode === "signin" 
                ? "Sign In" 
                : "Create an Account"}
        </CardTitle>
        <CardDescription>
          {showChangePassword 
            ? "Enter your new password below" 
            : showResetPassword 
              ? "Enter your email address to receive a password reset link" 
              : mode === "signin" 
                ? "Enter your email and password to sign in" 
                : "Enter your details to create an account"}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {showChangePassword ? (
          <div className="space-y-4">
            {changePasswordSuccess ? (
              <Alert className="bg-green-50 border-green-200">
                <KeyRound className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-600">
                  Your password has been updated successfully. You can now sign in with your new password.
                </AlertDescription>
              </Alert>
            ) : (
              <Form {...changePasswordForm}>
                <form onSubmit={changePasswordForm.handleSubmit(handleChangePassword)} className="space-y-4">
                  <FormField
                    control={changePasswordForm.control}
                    name="password"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>New Password</FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="••••••••" 
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={changePasswordForm.control}
                    name="confirmPassword"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Confirm Password</FormLabel>
                        <FormControl>
                          <Input 
                            type="password" 
                            placeholder="••••••••" 
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="flex justify-between gap-2">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => {
                        setShowChangePassword(false);
                        window.history.replaceState({}, document.title, "/auth");
                      }}
                      className="flex-1"
                    >
                      Back to Sign In
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1" 
                      disabled={changePasswordLoading}
                    >
                      {changePasswordLoading ? "Updating..." : "Update Password"}
                    </Button>
                  </div>
                </form>
              </Form>
            )}
          </div>
        ) : showResetPassword ? (
          <div className="space-y-4">
            {resetSent && (
              <Alert className="bg-green-50 border-green-200">
                <Mail className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-600">
                  Password reset link has been sent to your email. Please check your inbox and spam folder.
                </AlertDescription>
              </Alert>
            )}
            <Form {...resetForm}>
              <form onSubmit={resetForm.handleSubmit(handleResetPassword)} className="space-y-4">
                <FormField
                  control={resetForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input 
                          type="email" 
                          placeholder="name@example.com" 
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="flex justify-between gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => setShowResetPassword(false)}
                    className="flex-1"
                  >
                    Back to Sign In
                  </Button>
                  <Button 
                    type="submit" 
                    className="flex-1" 
                    disabled={resetLoading}
                  >
                    {resetLoading ? "Sending..." : "Send Reset Link"}
                  </Button>
                </div>
              </form>
            </Form>
          </div>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {mode === "signup" && (
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="firstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>First Name</FormLabel>
                        <FormControl>
                          <Input placeholder="John" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="lastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Last Name</FormLabel>
                        <FormControl>
                          <Input placeholder="Doe" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              )}
              {mode === "signup" && (
                <FormField
                  control={form.control}
                  name="role"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Account Type</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select your account type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="user">Student</SelectItem>
                          <SelectItem value="instructor">Teacher/Instructor</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input type="email" placeholder="name@example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Password</FormLabel>
                    <FormControl>
                      <Input type="password" placeholder="••••••••" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {mode === "signin" && (
                <div className="text-sm">
                  <Button 
                    variant="link" 
                    className="p-0 h-auto text-primary" 
                    type="button"
                    onClick={() => setShowResetPassword(true)}
                  >
                    Forgot your password?
                  </Button>
                </div>
              )}
              {mode === "signup" && (
                <FormField
                  control={form.control}
                  name="acceptTerms"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md pt-2">
                      <FormControl>
                        <Checkbox 
                          checked={field.value} 
                          onCheckedChange={field.onChange}
                        />
                      </FormControl>
                      <div className="space-y-1 leading-none">
                        <FormLabel>
                          I agree to the{" "}
                          <Link to="/terms" className="text-primary underline hover:text-primary/80" target="_blank" rel="noopener noreferrer">
                            Terms and Conditions
                          </Link>
                        </FormLabel>
                        <FormMessage />
                      </div>
                    </FormItem>
                  )}
                />
              )}
              
              {showCaptcha && (
                <div className="py-4">
                  <CustomCaptcha 
                    value={captchaValue} 
                    onChange={handleCaptchaChange} 
                    onValidityChange={handleCaptchaValidityChange}
                  />
                </div>
              )}
              
              <Button 
                type="submit" 
                className="w-full" 
                disabled={isLoading || (showCaptcha && !isCaptchaValid)}
              >
                {isLoading ? "Loading..." : mode === "signin" ? "Sign In" : "Create Account"}
              </Button>
            </form>
          </Form>
        )}
        <SpamFolderAlert show={showSpamAlert} />
      </CardContent>
      {!showResetPassword && !showChangePassword && (
        <CardFooter className="flex justify-center border-t pt-4">
          <Button variant="link" onClick={toggleMode}>
            {mode === "signin" ? "Don't have an account? Create one" : "Already have an account? Sign in"}
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};
